import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiRequest, HttpMethod } from '@api/common';
import { queryKeys, showApiError } from '@api/queryClient';
import toastService from '@services/toastService';
import { ProductType } from '~types/types';

// ============================================================================
// Types
// ============================================================================

export interface Mixture {
  id: string;
  productId: string;
  productName: string;
  productMixtureId: string;
  portation: number;
  terminalId: string;
  tankId: string;
  tankName: string;
}

export interface ProductMixture {
  id: string;
  code: string;
  name: string;
  customerId: string;
  customerName: string;
  mixtures: Mixture[];
  notes: string;
  description: string;
  productType: ProductType;
  isEnabled: boolean;
  waybillDescription: string;
}

export interface AddProductMixtureCmd {
  code: string;
  name: string;
  customerId: string;
  mixtures: MixtureDto[];
  notes: string;
  waybillDescription: string;
}

interface MixtureDto {
  productId: string;
  portation: number;
  tankId: string;
}

// ============================================================================
// API Functions
// ============================================================================

const API_ENDPOINT = 'productmixtures';

const fetchProductMixtures = async (): Promise<ProductMixture[]> => {
  return apiRequest<void, ProductMixture[]>(HttpMethod.GET, API_ENDPOINT);
};

const fetchProductMixture = async (id: string): Promise<ProductMixture> => {
  return apiRequest<void, ProductMixture>(HttpMethod.GET, `${API_ENDPOINT}/${id}`);
};

const createProductMixture = async (mixture: AddProductMixtureCmd): Promise<ProductMixture> => {
  return apiRequest<AddProductMixtureCmd, ProductMixture>(HttpMethod.POST, API_ENDPOINT, mixture);
};

const updateProductMixture = async (mixture: ProductMixture): Promise<ProductMixture> => {
  return apiRequest<ProductMixture, ProductMixture>(HttpMethod.PUT, `${API_ENDPOINT}/${mixture.id}`, mixture);
};

const deleteProductMixture = async (id: string): Promise<void> => {
  return apiRequest<void, void>(HttpMethod.DELETE, `${API_ENDPOINT}/${id}`);
};

// ============================================================================
// Query Hooks
// ============================================================================

/**
 * Fetch all product mixtures
 */
export const useProductMixturesQuery = () => {
  return useQuery({
    queryKey: queryKeys.productMixtures.all,
    queryFn: fetchProductMixtures,
  });
};

/**
 * Fetch a single product mixture by ID
 */
export const useProductMixtureQuery = (id: string | undefined) => {
  return useQuery({
    queryKey: [...queryKeys.productMixtures.all, 'detail', id],
    queryFn: () => fetchProductMixture(id!),
    enabled: !!id,
  });
};

// ============================================================================
// Mutation Hooks
// ============================================================================

/**
 * Create a new product mixture
 */
export const useCreateProductMixtureMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: createProductMixture,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.productMixtures.all });
      toastService.success({ title: 'save_succeeded' });
    },
    onError: (error: unknown) => {
      showApiError(error, 'save_failed');
    },
  });
};

/**
 * Update an existing product mixture
 */
export const useUpdateProductMixtureMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: updateProductMixture,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.productMixtures.all });
      toastService.success({ title: 'save_succeeded' });
    },
    onError: (error: unknown) => {
      showApiError(error, 'save_failed');
    },
  });
};

/**
 * Delete a product mixture
 */
export const useDeleteProductMixtureMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: deleteProductMixture,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.productMixtures.all });
      toastService.success({ title: 'product.product_removed' });
    },
    onError: (error: unknown) => {
      showApiError(error, 'save_failed');
    },
  });
};
